import { ArrowRight, Home, Target, Activity, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTrackEvent } from '../components/AnalyticsProvider';
import MarketingLayout from '../components/MarketingLayout';
import PageHero from '../components/PageHero';

export default function NotFoundPage() {
  const trackEvent = useTrackEvent();
  const handleSignUpClick = () =>
    trackEvent({ action: 'sign_up_click', category: '404_page', label: 'cta_button' });
  const handleSignInClick = () =>
    trackEvent({ action: 'sign_in_click', category: '404_page', label: 'sign_in_button' });
  return (
    <MarketingLayout onSignInClick={handleSignInClick} onSignUpClick={handleSignUpClick}>
      <PageHero
        badge={<span>404 · Page not found</span>}
        title="Let’s get you back on track."
        subtitle="This link may have changed, or the address may be incomplete. Your next step is still within reach: return home or open your strategic workspace."
      >
        <Link to="/" className="public-button public-button--primary">
          <Home className="h-5 w-5" />
          Return home
        </Link>
        <Link
          to="/sign-in"
          onClick={handleSignInClick}
          className="public-button public-button--secondary"
        >
          Sign in to my workspace <ArrowRight className="h-5 w-5" />
        </Link>
      </PageHero>
      <section className="public-section">
        <div className="public-container">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="mb-4">Find the path you need.</h2>
            <p className="text-xl">
              Explore the workflow, check where your strategy stands, or ask our team for help.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Target,
                title: 'Explore the strategic framework',
                description:
                  'Follow the five-step path from your company’s direction to projects and tasks.',
                to: '/strategic-framework',
                label: 'Explore the framework',
              },
              {
                icon: Activity,
                title: 'Take a fresh look at your strategy',
                description:
                  'Use the 12-point health check to identify where your next focus could make a difference.',
                to: '/health-check',
                label: 'Take the health check',
              },
              {
                icon: Mail,
                title: 'Get help finding your way',
                description:
                  'Tell us what you were looking for so we can point you to the right place.',
                to: '/contact',
                label: 'Contact our team',
              },
            ].map(({ icon: Icon, title, description, to, label }) => (
              <article key={to} className="public-card p-7">
                <Icon className="h-8 w-8 text-blue-600 mb-5" />
                <h3 className="mb-3">{title}</h3>
                <p className="text-gray-600 mb-5">{description}</p>
                <Link
                  to={to}
                  className="text-blue-600 font-semibold inline-flex items-center gap-2"
                >
                  {label}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="public-cta">
        <div className="public-container text-center">
          <h2 className="mb-4">A clearer plan starts with your next move.</h2>
          <p className="max-w-2xl mx-auto mb-7">
            Bring your vision, priorities, people and projects into one connected strategic
            workspace.
          </p>
          <Link
            to="/sign-up"
            onClick={handleSignUpClick}
            className="public-button public-button--primary"
          >
            Start my strategic plan <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
